import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import Text from "~/components-shared/Text";
import Card from "~/components-shared/Card/Card";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { AuthoritiesEnum, CRUDActionEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import Grid from "~/components-shared/Grid";
import Picture from "~/components-shared/Picture";
import Link from "~/components-shared/Link";
import Icon from "~/components-shared/Icon";
import Dropdown from "~/components-shared/Dropdown";
import { useDispatch, useSelector } from "react-redux";
import requestsActionCreators from "~/store/requests/actions";
import styled, { useTheme } from "styled-components";
import authSelectors from "~/store/auth/selectors";
import requestSelectors from "~/store/requests/selectors";
import useAbacExt from "~/hooks/abac";
import Markdown from "~/components/Markdown";
import moment from "moment/moment";
const ActionsDropdown = styled(Dropdown)`
  position: absolute;
  top: 15px;
  right: 10px;
`;
const RequestCard = _ref => {
  let {
    children,
    request,
    ...rest
  } = _ref;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const user = useSelector(authSelectors.getUser);
  const status = useSelector(requestSelectors.getStatusByRequest(request.id));
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const handleView = () => {
    history.push(RoutesEnum.REQUEST.replace(":requestId", request.id));
  };
  const handleEdit = e => {
    e.stopPropagation();
    dispatch(requestsActionCreators.actionChanged(CRUDActionEnum.UPDATE, request.id));
  };
  const handleDelete = e => {
    e.stopPropagation();
    dispatch(requestsActionCreators.actionChanged(CRUDActionEnum.DELETE, request.id));
  };
  const handleCommunityClick = e => {
    e.stopPropagation();
    history.push(RoutesEnum.COMMUNITY.replace(":communityId", request.community.id));
  };
  const handleAuthorClick = e => {
    e.stopPropagation();
    history.push(RoutesEnum.MEMBER.replace(":memberId", request.createdBy.id));
  };
  const handleVolunteer = e => {
    e.stopPropagation();
    dispatch(requestsActionCreators.volunteered(request.id));
  };
  const handleRevoke = e => {
    e.stopPropagation();
    dispatch(requestsActionCreators.revoked(request.id));
  };
  const handleLike = e => {
    e.stopPropagation();
    dispatch(requestsActionCreators.liked(request.id));
  };
  const handleUnlike = e => {
    e.stopPropagation();
    dispatch(requestsActionCreators.unliked(request.id));
  };
  const volunteersReached = request.capVolunteers && request.volunteers.length >= request.volunteerNumber;
  const dateExpired = moment(request.date).valueOf() <= moment().valueOf();
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 7
    },
    border: true,
    onClick: handleView
  }, rest), !request.closedAt && userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(ActionsDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    })
  }, _ref2 => {
    let {
      isOpen,
      onItemClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        handleEdit(e);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.REQUESTS_WRITE], request.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        handleDelete(e);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 7,
    template: "8.6rem auto"
  }, /*#__PURE__*/React.createElement(Picture, {
    src: request.community.picture,
    name: request.community.name,
    color: request.community.color
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Link, {
    onClick: handleCommunityClick,
    weight: "semiBold",
    fontSize: 4,
    color: colors.text.primary
  }, "@", request.community.name), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "light",
    s: {
      ml: 2
    }
  }, t("RequestCard.sharedBy"), " ", /*#__PURE__*/React.createElement(Link, {
    onClick: handleAuthorClick,
    color: "textSecondary"
  }, request.createdBy.firstName, " ", request.createdBy.lastName)), /*#__PURE__*/React.createElement(Text, {
    element: "h2",
    s: {
      mb: 3,
      mt: 1
    }
  }, request.name), request.description ? /*#__PURE__*/React.createElement(Text, {
    element: "p",
    s: {
      mb: 2
    },
    fontSize: 4,
    lineHeight: 7
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: request.description
  })) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 3
    },
    display: "block",
    color: "textSecondary"
  }, t("RequestCard.noDescription")), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary",
    weight: "light",
    display: "block"
  }, t("RequestCard.needed", {
    context: request.capVolunteers ? "cap" : "",
    amount: request.volunteerNumber,
    date: moment(request.date).format("ddd, MMMM D hh:mma")
  })), request.likes.find(v => v.id === user?.id) ? /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RequestCard.likes"),
    display: "inline-block",
    onClick: handleUnlike,
    color: "transparent",
    s: {
      px: 3,
      py: 1,
      ml: -3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.danger.main,
    src: "heart"
  }), " ", request.likes.length) : /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RequestCard.likes"),
    display: "inline-block",
    onClick: handleLike,
    color: "transparent",
    s: {
      px: 3,
      py: 1,
      ml: -3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.secondary.main,
    src: "heart"
  }), " ", request.likes.length), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RequestCard.comments"),
    display: "inline-block",
    onClick: handleView,
    color: "transparent",
    s: {
      px: 3,
      py: 1
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.text.primary,
    src: "message"
  }), " ", request.commentCount), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RequestCard.volunteers"),
    display: "inline-block",
    onClick: handleView,
    color: "transparent",
    s: {
      px: 3,
      py: 1
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    color: colors.text.primary,
    scale: 3
  }), " ", request.volunteers.length, " ", /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: colors.secondary.main
  }, "(", request.volunteers.filter(attendee => attendee.active).length, ")")), /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    justify: "end",
    s: {
      mt: 3
    }
  }, /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, request.closedAt ? /*#__PURE__*/React.createElement(Button, {
    color: "info",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    disabled: true,
    rounded: true,
    display: "inline-block"
  }, t("Request.closed")) : !volunteersReached && !dateExpired ? request.volunteers.find(v => v.id === user?.id) ? /*#__PURE__*/React.createElement(Button, {
    onClick: handleRevoke,
    color: "dangerGrey",
    s: {
      ml: 3,
      px: 7,
      py: 2
    },
    rounded: true,
    display: "inline-block",
    loading: status === StatusStateEnum.LOADING
  }, t("common.actions.revoke")) : /*#__PURE__*/React.createElement(Button, {
    onClick: handleVolunteer,
    color: "textSecondary",
    s: {
      ml: 3,
      px: 7,
      py: 2
    },
    rounded: true,
    display: "inline-block",
    loading: status === StatusStateEnum.LOADING
  }, t("common.actions.volunteer")) : /*#__PURE__*/React.createElement(React.Fragment, null, volunteersReached && /*#__PURE__*/React.createElement(Button, {
    color: "info",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    disabled: true,
    rounded: true,
    display: "inline-block"
  }, t("Request.amountReached")), dateExpired && /*#__PURE__*/React.createElement(Button, {
    color: "info",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    disabled: true,
    rounded: true,
    display: "inline-block"
  }, t("Request.dateExceeded"))))))));
};
export default RequestCard;